/**
 * Credentials — the pane where a user hands over a secret. Rendered as the
 * Credentials tab of Apps & Skills; it lives in this directory because it is
 * built from the settings primitives, not because it is a settings section.
 *
 * Any secret, not just an exchange API key: a data-provider token, a webhook
 * signing key, a bearer token for an internal service. Nothing here is specific
 * to trading venues, and the wording deliberately does not imply otherwise —
 * the mechanism is "a string the agent must not see, sent only to hosts you
 * name".
 *
 * This is the one screen in the terminal that handles a live secret, so its
 * behaviour is unusual in three ways and each is deliberate:
 *
 *  1. **The secret is encrypted in this browser and leaves already opaque.**
 *     `config.credentials.store` seals it (see `sealCredential` in
 *     `@paradigm/terminal-core`); the backend that receives it cannot decrypt
 *     it, and neither can anything else except the proxy pod belonging to this
 *     user's own agent.
 *  2. **The plaintext lives in one `useState` and is cleared the moment the
 *     store call returns**, success or failure. It is never put in
 *     preferences, chat history, a URL, or an error message. There is no "show
 *     what you typed" affordance after saving, and no way to read a stored
 *     credential back — not because it is hidden, but because nothing here can.
 *  3. **Destinations are required, not optional.** A secret with no host is
 *     never substituted anywhere, so accepting one would leave the user with
 *     something that looks stored and silently does nothing.
 *
 * What a stored secret gives back is a *placeholder* — a string the
 * user's agent writes where the secret belongs, and which the proxy swaps for
 * the real value on the way out. It is NOT itself a secret (it is displayed
 * here, copied, and pasted into conversations); its random component only makes
 * it distinct per stored secret.
 *
 * Storing a secret publishes that placeholder into the agent's environment as
 * `CRED_<LABEL>`, so the user normally carries nothing: the list shows the
 * placeholder so they can see what their agent will use, and the copy button
 * is there for the case where publishing failed (`agentReady` false).
 *
 * ONE delivery editor, with the rare controls behind a disclosure. The
 * default needs nothing typed, because replace mode already means "swap the
 * placeholder wherever it appears in a request header" — the proxy scans them
 * all unless told to narrow. The checkboxes ADD places beyond headers, and
 * More options reveals the two things almost nobody needs: narrowing the
 * header scan, and inject mode with its destination and formatter. Between
 * them the form reaches every delivery the API accepts, so no credential is
 * readable here but uneditable.
 *
 * A row says something about readiness only when the agent does NOT have it:
 * a secret the agent already knows needs no announcement, and the absence of a
 * marker is the ordinary case. `agentReady` is three-valued and only false
 * renders — UNDEFINED means the server could not ask (a pod mid-rollout), and
 * a warning on every secret whenever an agent restarts would be both untrue
 * and the kind of thing a user acts on.
 */
import { Fragment, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { useTerminalConfig } from '../../context/config-context';
import { looseIncludes } from '../search-match';
import type { CredentialDelivery, StoredCredentialView } from '../../config';
import {
  FieldColumn,
  FieldError,
  FieldRow,
  FieldStatus,
  PaneTitle,
  Row,
  RowHelp,
  RowLabel,
  RowText,
  SaveButton,
  TextInput,
  TYPE,
} from './primitives';
import { SettingsSelect } from './SettingsSelect';

/**
 * How the secret reaches the request, as a small set of choices rather than the
 * underlying config.
 *
 * The proxy's own config is a header name, an optional Go template, and four
 * match flags. Exposing that raw would ask a user to type `Bearer {{ .Value }}`
 * into a settings form, so the common shapes are presets and only the parts that
 * genuinely vary — a custom header or parameter name — are typed.
 *
 * The first four are inject mode: the proxy attaches the secret to requests
 * bound for an allowed host and the agent sends nothing at all. The last two are
 * replace mode, for what inject cannot express — chiefly a secret that belongs
 * inside a request body.
 */
/**
 * The one line under "Where it goes". The controls beneath it say the rest.
 */
const DELIVERY_HELP =
  'Your agent writes the placeholder; the proxy swaps it for the real secret.';

/**
 * Every place the proxy acts on, in reading order.
 *
 * ALL of them, which the previous version of this did not do: it showed
 * `matchHeaders[0]` or the body, so a credential matching two headers, or a
 * header AND the body, or the URL path, was described by naming one of them
 * and silently dropping the rest. A summary that omits half the rule is worse
 * than none — a user checking where their key can end up would have been
 * reassured by an answer that was not true.
 */
function deliveryTargets(d: CredentialDelivery): readonly string[] {
  if (d.mode === 'replace') {
    const out = [...(d.matchHeaders ?? [])];
    if (d.matchBody === true) out.push('the request body');
    if (d.matchPath === true) out.push('the URL path');
    if (d.matchQuery === true) out.push('the query string');
    // HEADERS ARE ALWAYS SCANNED unless match_headers narrows them, and that
    // is a property of the header field ALONE — not of whether the rule
    // mentions anything else.
    //
    // Keying this off an empty result was wrong one case over: with
    // {mode:'replace', matchBody:true} the list is non-empty, so the branch
    // was skipped and the summary read "Swapped in: the request body" —
    // omitting that every header is scanned too. Keyed off the header
    // narrowing instead, and unshifted so the broadest target reads first.
    if ((d.matchHeaders ?? []).length === 0) out.unshift('any request header');
    return out;
  }
  if (d.queryParam != null && d.queryParam !== '') {
    return [`?${d.queryParam}=`];
  }
  return d.header != null && d.header !== '' ? [`${d.header} header`] : [];
}

/** "a", "a and b", "a, b and c" — for reading, not for parsing. */
function joinList(items: readonly string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** A stored credential's delivery, in one readable line for the list. */
function describeDelivery(d: CredentialDelivery): string {
  const targets = joinList(deliveryTargets(d));
  // Only reachable for an INJECT row with neither a header nor a query
  // parameter, which both the API and this pane refuse. Stated rather than
  // hidden: a row that somehow has one is doing nothing, and the user should
  // be able to see that. Replace mode never lands here — no narrowing means
  // every header, not nowhere.
  if (targets === '') return 'no destination';
  if (d.mode === 'replace') return `placeholder in ${targets}`;
  return d.formatter != null && d.formatter !== ''
    ? `${targets}, formatted`
    : targets;
}

/**
 * The whole stored config, as label/value pairs.
 *
 * The one-line summary above is for scanning a list; this is for answering
 * "what exactly did I save". It is the only place the full rule is visible, so
 * it is also what makes the not-editable-here rows honest: a config the presets
 * cannot express can at least be READ, which matters more than editing it.
 */
function credentialDetails(
  c: StoredCredentialView,
): readonly { readonly label: string; readonly value: string }[] {
  const rows: { label: string; value: string }[] = [
    {
      label: 'Delivery',
      value:
        c.delivery.mode === 'replace'
          ? 'replace — your agent writes the placeholder, the proxy swaps it'
          : 'inject — the proxy attaches the secret, your agent sends nothing',
    },
    { label: 'Hosts', value: c.allowedHosts.join(', ') || 'none' },
    {
      label: 'Paths',
      // "Any path" rather than "none": an empty list is the widest setting, and
      // labelling the widest thing "none" reads as the narrowest.
      value: c.allowedPaths.length > 0 ? c.allowedPaths.join(', ') : 'any path',
    },
    {
      label: c.delivery.mode === 'replace' ? 'Swapped in' : 'Written to',
      value: joinList(deliveryTargets(c.delivery)) || 'nowhere',
    },
  ];
  // Inject only — the type is a discriminated union and replace has no
  // formatter, which is the whole reason the mode check is not redundant.
  if (
    c.delivery.mode === 'inject' &&
    c.delivery.formatter != null &&
    c.delivery.formatter !== ''
  ) {
    // In the same dialect the form uses, so what is read here is what would be
    // typed there. formatterToField is a faithful rendering rather than a
    // paraphrase — and it leaves anything it cannot express exactly alone.
    rows.push({
      label: 'Wrapped as',
      value: formatterToField(c.delivery.formatter),
    });
  }
  if (c.placeholder !== '') {
    // Where the agent finds it. Deliberately not on every row — see the
    // agentReady note at the top of the file — but exactly what someone
    // opening the details is asking for.
    rows.push({ label: 'Agent variable', value: `$${agentEnvName(c.label)}` });
  }
  rows.push({ label: 'Added', value: formatAdded(c.createdAt) });
  return rows;
}

/**
 * The environment variable a stored credential reaches the agent as. Mirrors
 * AgentEnvName in api/terminal/web-api/services/agentenv.go — labels are
 * [a-z0-9-], so this is the same string on both sides.
 */
function agentEnvName(label: string): string {
  return `CRED_${label.toUpperCase().replace(/-/g, '_')}`;
}

/** The server's RFC3339 timestamp, or the raw string if it will not parse. */
function formatAdded(createdAt: string): string {
  const t = Date.parse(createdAt);
  return Number.isNaN(t) ? createdAt : new Date(t).toLocaleDateString();
}

/**
 * Mirrors the server's own label rule (`[a-z0-9-]`, 1-32). Checked here so a
 * mistake is caught while the field is in front of the user — but the server
 * checks it too, and the server is the authority. The narrow rule is not
 * cosmetic: the label is part of what the credential is cryptographically bound
 * to, so a label needing normalization is a credential that fails to open
 * later.
 */
const LABEL_PATTERN = /^[a-z0-9][a-z0-9-]{0,31}$/;

/** Same reasoning: a bare hostname, matched exactly by the proxy. */
const HOST_PATTERN =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

type LoadState =
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'ready';
      readonly credentials: readonly StoredCredentialView[];
    }
  | { readonly kind: 'error'; readonly message: string };

type FormState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'saving' }
  /**
   * The store succeeded but something after it did not — today, publishing the
   * placeholder to the agent. Distinct from 'error' because the secret IS
   * stored: the form clears, and the message says what the user has to do
   * instead of implying they should try again.
   */
  | { readonly kind: 'warning'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

/** Splits the hosts field on commas and whitespace, dropping empties. */
function parseHosts(raw: string): readonly string[] {
  return raw
    .split(/[\s,]+/)
    .map((h) => h.trim().toLowerCase())
    .filter((h) => h.length > 0);
}

/**
 * Splits the paths field, preserving case.
 *
 * Not parseHosts, which lowercases: a hostname is case-insensitive on the wire
 * and a path is not, so rewriting /Orders to /orders would produce a pattern
 * that matches nothing.
 */
function parsePaths(raw: string): readonly string[] {
  return raw
    .split(/[\s,]+/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
}

/**
 * A header-name token (RFC 7230) and a query-parameter name, mirroring
 * headerNamePattern and queryParamPattern in services/credentials.go. Checked
 * here so a typo is caught in front of the user; the server checks too and is
 * the authority.
 */
const HEADER_NAME_PATTERN = /^[A-Za-z0-9!#$%&'*+.^_`|~-]{1,64}$/;
const QUERY_PARAM_PATTERN = /^[A-Za-z0-9_.-]{1,64}$/;

/** maxMatchHeaders and maxAllowedHostsPerCredential in services/credentials.go. */
const MAX_MATCH_HEADERS = 5;
const MAX_HOSTS = 10;

/** maxAllowedPathsPerCredential in services/credentials.go. */
const MAX_PATHS = 20;

/**
 * A request path, optionally with the proxy's glob syntax. Mirrors pathPattern
 * in services/credentials.go: rooted, and carrying no host, query string or
 * whitespace — anything else could not have been meant and would silently
 * never match.
 */
const PATH_PATTERN = /^\/[A-Za-z0-9._~!$&'()*+,;=:@%/-]*$/;

/**
 * What a user types where the secret goes, and what the proxy actually wants.
 *
 * iron-proxy renders the formatter as a GO TEMPLATE with `.Value` bound to the
 * secret, so `{{ .Value }}` is the real wire format and what gets stored. It is
 * also four kinds of punctuation to type correctly in a field with no syntax
 * help, for a substitution that could be spelt with one word.
 *
 * So the form speaks $VALUE and translates at its own edge. The stored value is
 * always the Go template — nothing downstream learns a second dialect, and an
 * API caller still uses `{{ .Value }}` (a bare $VALUE fails the server's
 * "must mention .Value" check rather than silently sending a constant).
 */
const VALUE_TOKEN = '$VALUE';

/** `{{ .Value }}` with any inner spacing, which Go templates allow. */
const GO_VALUE_RE = /\{\{\s*\.Value\s*\}\}/g;

/**
 * The stored formatter as the field should show it.
 *
 * Shown in the $VALUE spelling only when that spelling means the same thing,
 * which is decided by translating it back and comparing. Two cases fail that
 * test and are shown verbatim instead:
 *
 *  - The stored template already contains a LITERAL `$VALUE`, e.g.
 *    `{{ .Value }} kid-$VALUE`. Translating gives `$VALUE kid-$VALUE`, in
 *    which the two are indistinguishable, and saving that back would store
 *    `{{ .Value }} kid-{{ .Value }}` — the field would have silently added a
 *    second place the secret is written.
 *  - Anything using template syntax the $VALUE spelling cannot express. The
 *    server refuses those now, but rows enrolled before it did are still
 *    readable, and showing such a row half-rewritten would be worse than
 *    showing all of it.
 *
 * Verbatim text always contains `{{`, so formatterToStored passes it back
 * unchanged and an untouched row survives a save exactly as stored.
 */
function formatterToField(stored: string): string {
  const friendly = stored.replace(GO_VALUE_RE, VALUE_TOKEN);
  return formatterToStored(friendly) === stored ? friendly : stored;
}

/** The field's text as the formatter to store. Raw templates pass through. */
function formatterToStored(typed: string): string {
  if (typed.includes('{{')) return typed;
  return typed.split(VALUE_TOKEN).join('{{ .Value }}');
}

/** Where inject writes the secret. Exactly one, which the API enforces too. */
type InjectVia = 'header' | 'query-param';

const INJECT_VIA_OPTIONS: readonly { value: InjectVia; label: string }[] = [
  { value: 'header', label: 'A request header' },
  { value: 'query-param', label: 'A query parameter' },
];

const MODE_OPTIONS: readonly {
  value: CredentialDelivery['mode'];
  label: string;
}[] = [
  { value: 'replace', label: 'Your agent writes a placeholder' },
  { value: 'inject', label: 'The proxy attaches the secret' },
];

/**
 * The delivery config being edited, as fields rather than as a delivery.
 *
 * HEADERS ARE NOT A FIELD HERE, and that is the point. In replace mode the
 * proxy scans every request header for the placeholder unconditionally —
 * internal/transform/secrets/secrets.go calls swapHeaders on every replace
 * entry, and swapHeaders with no matchers walks the whole header map. So "in a
 * header" is not something a user opts into or names; it is what always
 * happens. `narrow` exists to RESTRICT that scan to named headers, which is
 * the rare case, and everything else here ADDS places beyond headers.
 *
 * An earlier version of this form asked for a header name and refused to save
 * without one. That was a header name the proxy did not need, in a required
 * field, on the default path.
 *
 * Strings and booleans rather than a CredentialDelivery so a half-typed header
 * name is representable; buildDelivery is what turns it into a delivery, or
 * null while it is not valid.
 */
interface DeliveryDraft {
  readonly mode: CredentialDelivery['mode'];
  readonly narrow: boolean;
  readonly headersRaw: string;
  readonly body: boolean;
  readonly path: boolean;
  readonly query: boolean;
  readonly via: InjectVia;
  readonly name: string;
  readonly formatter: string;
  /**
   * Whether the destination name has been typed in.
   *
   * Switching to inject mode leaves it empty, which is not a mistake the user
   * has made — so it must not turn red the instant they choose the mode. The
   * disabled Store button and the field's own "Required" hint say the form is
   * incomplete; the error only names the field once they have been in it.
   */
  readonly nameTouched: boolean;
}

const EMPTY_DRAFT: DeliveryDraft = {
  mode: 'replace',
  narrow: false,
  headersRaw: '',
  body: false,
  path: false,
  query: false,
  via: 'header',
  name: '',
  formatter: '',
  nameTouched: false,
};

/** Fills the fields from a stored delivery, so an edit starts from the truth. */
function draftFrom(d: CredentialDelivery | undefined): DeliveryDraft {
  if (d == null) return EMPTY_DRAFT;
  if (d.mode === 'replace') {
    const headers = d.matchHeaders ?? [];
    return {
      ...EMPTY_DRAFT,
      mode: 'replace',
      narrow: headers.length > 0,
      headersRaw: headers.join(', '),
      body: d.matchBody === true,
      path: d.matchPath === true,
      query: d.matchQuery === true,
    };
  }
  const viaQuery = d.queryParam != null && d.queryParam !== '';
  return {
    ...EMPTY_DRAFT,
    mode: 'inject',
    via: viaQuery ? 'query-param' : 'header',
    name: (viaQuery ? d.queryParam : d.header) ?? '',
    formatter: formatterToField(d.formatter ?? ''),
    // Stored means typed: an existing credential's destination is the user's.
    nameTouched: true,
  };
}

/**
 * Splits a comma/whitespace list, preserving case.
 *
 * Not parseHosts, which lowercases: a header name is case-insensitive on the
 * wire but stored as typed, and rewriting "X-Api-Key" to "x-api-key" would show
 * the user something they did not enter for no gain.
 */
function parseList(raw: string): readonly string[] {
  return raw
    .split(/[\s,]+/)
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}

/** Case-insensitive dedupe, keeping the first spelling — as the server does. */
function dedupeHeaders(headers: readonly string[]): readonly string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const h of headers) {
    const key = h.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(h);
  }
  return out;
}

/** The narrowing list, empty when the user has not asked to narrow. */
function draftHeaders(d: DeliveryDraft): readonly string[] {
  return d.narrow ? dedupeHeaders(parseList(d.headersRaw)) : [];
}

/**
 * The draft as a delivery, or null when the API would refuse it. Mirrors
 * canonicalDelivery: inject needs exactly one destination, a formatter must
 * mention .Value, and at most five headers may be named. Replace needs nothing
 * — headers are always scanned.
 */
function buildDelivery(d: DeliveryDraft): CredentialDelivery | null {
  if (d.mode === 'replace') {
    const headers = draftHeaders(d);
    if (d.narrow && headers.length === 0) return null;
    if (headers.length > MAX_MATCH_HEADERS) return null;
    if (headers.some((h) => !HEADER_NAME_PATTERN.test(h))) return null;
    return {
      mode: 'replace',
      ...(headers.length > 0 ? { matchHeaders: headers } : {}),
      ...(d.body ? { matchBody: true } : {}),
      ...(d.path ? { matchPath: true } : {}),
      ...(d.query ? { matchQuery: true } : {}),
    };
  }
  const name = d.name.trim();
  const formatter = formatterToStored(d.formatter.trim());
  // Same rule as the server, applied to the STORED form: a formatter that
  // never mentions the secret sends a constant, and looks saved.
  if (formatter !== '' && !formatter.includes('.Value')) return null;
  if (d.via === 'query-param') {
    if (!QUERY_PARAM_PATTERN.test(name)) return null;
    return {
      mode: 'inject',
      queryParam: name,
      ...(formatter !== '' ? { formatter } : {}),
    };
  }
  if (!HEADER_NAME_PATTERN.test(name)) return null;
  return {
    mode: 'inject',
    header: name,
    ...(formatter !== '' ? { formatter } : {}),
  };
}

/** The one thing wrong with the draft, or null. */
function draftProblem(d: DeliveryDraft): string | null {
  if (d.mode === 'replace') {
    if (!d.narrow) return null;
    const headers = draftHeaders(d);
    const bad = headers.find((h) => !HEADER_NAME_PATTERN.test(h));
    if (bad != null) return `“${bad}” isn’t a header name.`;
    if (headers.length > MAX_MATCH_HEADERS) {
      return `At most ${MAX_MATCH_HEADERS} headers.`;
    }
    if (headers.length === 0) return 'Name at least one header.';
    return null;
  }
  const name = d.name.trim();
  if (name === '') {
    return d.nameTouched
      ? `${d.via === 'query-param' ? 'A parameter' : 'A header'} name is required.`
      : null;
  }
  const ok =
    d.via === 'query-param'
      ? QUERY_PARAM_PATTERN.test(name)
      : HEADER_NAME_PATTERN.test(name);
  if (!ok) {
    return d.via === 'query-param'
      ? `“${name}” isn’t a parameter name.`
      : `“${name}” isn’t a header name.`;
  }
  const typed = d.formatter.trim();
  if (typed !== '' && !formatterToStored(typed).includes('.Value')) {
    return `A format must contain ${VALUE_TOKEN}, or the secret is never written.`;
  }
  return null;
}

/**
 * The "where may it go / how does it get there" half of the form, which the add
 * form and the edit form share.
 *
 * Shared as state-plus-component rather than copied, because these are the
 * fields with rules, and two copies of a validation rule is one rule that will
 * eventually disagree with itself. Everything the two forms do differently (a
 * label and a secret on one, an id and Cancel on the other) stays outside.
 *
 * ONE editor, not two. `advanced` is a disclosure — it reveals the mode switch,
 * the header narrowing and inject's fields — so nothing is held in a second
 * state that a toggle could discard. An earlier version had a simple editor and
 * an advanced one with separate state, and the question "what happens to my
 * config when I switch" had no good answer.
 */
function useDestination(initial?: {
  readonly hosts: readonly string[];
  readonly paths: readonly string[];
  readonly delivery: CredentialDelivery;
}) {
  const [hostsRaw, setHostsRaw] = useState(initial?.hosts.join(', ') ?? '');
  const [pathsRaw, setPathsRaw] = useState(initial?.paths.join(', ') ?? '');
  const [draft, setDraft] = useState<DeliveryDraft>(() =>
    draftFrom(initial?.delivery),
  );
  // Opens revealed when the stored config uses something only the advanced
  // controls can show, so an edit never hides what it is about to save.
  const [advanced, setAdvanced] = useState(() => {
    const d = draftFrom(initial?.delivery);
    return d.mode === 'inject' || d.narrow;
  });

  const hosts = parseHosts(hostsRaw);
  const badHost = hosts.find((h) => !HOST_PATTERN.test(h));
  const tooManyHosts = hosts.length > MAX_HOSTS;
  const hostsValid = hosts.length > 0 && badHost == null && !tooManyHosts;

  // Empty is valid here and invalid for hosts, which is the whole difference
  // between the two fields: hosts say where this may go, paths only narrow it.
  const paths = parsePaths(pathsRaw);
  const badPath = paths.find((p) => !PATH_PATTERN.test(p));
  const tooManyPaths = paths.length > MAX_PATHS;
  const pathsValid = badPath == null && !tooManyPaths;

  const delivery = buildDelivery(draft);

  return {
    hostsRaw,
    setHostsRaw,
    hosts,
    badHost,
    tooManyHosts,
    pathsRaw,
    setPathsRaw,
    paths,
    badPath,
    tooManyPaths,
    draft,
    advanced,
    /**
     * Collapsing DISCARDS the fields the disclosure hides.
     *
     * Only "Only these headers" and its header list are hidden by collapsing,
     * and leaving them in the draft meant a config could be saved from behind
     * a closed disclosure: tick it, type two header names, press Fewer
     * options, and the form shows a credential swapped in any header while
     * saving one restricted to two — with nothing on screen to say so. The
     * fix is not to hide state but to drop it, so what is submitted is what
     * is visible.
     *
     * The delivery MODE is deliberately not reset. Collapsing hides the mode
     * dropdown, but inject's own fields stay on screen and editable, so
     * nothing about that choice is hidden — and silently reverting a stored
     * inject credential to replace on a stray click of Fewer options would
     * destroy a working config rather than protect one.
     */
    toggleAdvanced: () => {
      if (advanced)
        setDraft((prev) => ({ ...prev, narrow: false, headersRaw: '' }));
      setAdvanced(!advanced);
    },
    problem: draftProblem(draft),
    delivery,
    valid: hostsValid && pathsValid && delivery != null,
    edit: (patch: Partial<DeliveryDraft>) =>
      setDraft((prev) => ({ ...prev, ...patch })),
    clear: () => {
      setHostsRaw('');
      setPathsRaw('');
      setDraft(EMPTY_DRAFT);
      setAdvanced(false);
    },
  };
}

type Destination = ReturnType<typeof useDestination>;

/**
 * A control inside the delivery column, with a VISIBLE label and an
 * explanation.
 *
 * primitives' Row puts one label in the left column beside one control, which is
 * every other setting in the pane. Delivery is several controls under one
 * heading, and the first cut simply stacked them: three unlabelled boxes, of
 * which a reader could guess the first from its placeholder and not the rest.
 * An aria-label is not a substitute — it tells a screen reader and nobody
 * looking at the screen.
 */
function LabelledField({
  label,
  hint,
  htmlFor,
  children,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly htmlFor?: string;
  readonly children: React.ReactNode;
}) {
  return (
    <FieldGroup>
      <FieldLabel as={htmlFor != null ? 'label' : 'span'} htmlFor={htmlFor}>
        {label}
      </FieldLabel>
      {children}
      {hint != null && <FieldNote>{hint}</FieldNote>}
    </FieldGroup>
  );
}

function DestinationFields({
  dest,
  idPrefix,
  selectLabel,
  scopeName,
  onDirty,
}: {
  readonly dest: Destination;
  /** Distinguishes the add form's ids from an open edit form's. */
  readonly idPrefix: string;
  /**
   * The mode select's accessible name. Passed in rather than fixed because the
   * add form and an open edit form are on screen at the same time, and two
   * controls with the same accessible name are ambiguous to anyone navigating
   * by label — a screen reader included.
   */
  readonly selectLabel: string;
  /** Names this form in the Advanced toggle's accessible name, for the same reason. */
  readonly scopeName?: string;
  /** Called on any edit, so the owning form can drop a stale message. */
  readonly onDirty: () => void;
}) {
  const { draft } = dest;
  const scope = scopeName != null ? ` for ${scopeName}` : '';
  const hostsId = `${idPrefix}-hosts`;
  const pathsId = `${idPrefix}-paths`;
  const set = (patch: Partial<DeliveryDraft>): void => {
    dest.edit(patch);
    onDirty();
  };
  return (
    <>
      <Row>
        <RowText>
          <RowLabel as="label" htmlFor={hostsId}>
            Send it to
          </RowLabel>
          <RowHelp>
            The hostnames this secret may be sent to, separated by commas.
          </RowHelp>
        </RowText>
        <FieldColumn>
          <TextInput
            id={hostsId}
            value={dest.hostsRaw}
            placeholder="api.binance.com"
            autoComplete="off"
            spellCheck={false}
            aria-invalid={dest.badHost != null || dest.tooManyHosts}
            onChange={(e) => {
              dest.setHostsRaw(e.target.value);
              onDirty();
            }}
          />
          <FieldStatus aria-live="polite">
            {dest.badHost != null && (
              <FieldError>
                “{dest.badHost}” isn’t a hostname — no https://, no port, no
                path.
              </FieldError>
            )}
            {dest.badHost == null && dest.tooManyHosts && (
              <FieldError>At most {MAX_HOSTS} hostnames.</FieldError>
            )}
          </FieldStatus>
        </FieldColumn>
      </Row>

      <Row>
        <RowText>
          <RowLabel as="label" htmlFor={pathsId}>
            Only on these paths
          </RowLabel>
          <RowHelp>
            Optional. Leave empty to use it anywhere on those hosts. End a path
            with /* to cover everything beneath it.
          </RowHelp>
        </RowText>
        <FieldColumn>
          <TextInput
            id={pathsId}
            value={dest.pathsRaw}
            placeholder="/api/v3/order, /sapi/v1/*"
            autoComplete="off"
            spellCheck={false}
            aria-invalid={dest.badPath != null || dest.tooManyPaths}
            onChange={(e) => {
              dest.setPathsRaw(e.target.value);
              onDirty();
            }}
          />
          <FieldStatus aria-live="polite">
            {dest.badPath != null && (
              <FieldError>
                “{dest.badPath}” isn’t a path — start with /, and leave out the
                hostname and any ?query.
              </FieldError>
            )}
            {dest.badPath == null && dest.tooManyPaths && (
              <FieldError>At most {MAX_PATHS} paths.</FieldError>
            )}
          </FieldStatus>
        </FieldColumn>
      </Row>

      <Row>
        <RowText>
          <RowLabel as="span">Where it goes</RowLabel>
          <RowHelp>{DELIVERY_HELP}</RowHelp>
        </RowText>
        <DeliveryColumn>
          {/* The mode select leads, because it decides what everything below
              it means. It was rendered last, under the fields it governs,
              which read as three unexplained boxes followed by a dropdown. */}
          {dest.advanced && (
            <LabelledField
              label="Delivery"
              hint={
                draft.mode === 'replace'
                  ? 'Your agent writes a placeholder and the proxy swaps it.'
                  : 'The proxy injects the secret and the appropriate header. Your agent must deliberately omit it.'
              }
            >
              <SettingsSelect
                label={selectLabel}
                value={draft.mode}
                options={MODE_OPTIONS}
                onChange={(mode) => set({ mode, nameTouched: false })}
              />
            </LabelledField>
          )}

          {draft.mode === 'replace' ? (
            <>
              {/* Stated, not offered: the proxy scans headers whether or not
                  anyone ticks anything, so a checkbox for it would be a
                  control that does nothing when cleared. */}
              <FieldNote>
                Swapped wherever it appears in a <strong>request header</strong>
                .
              </FieldNote>
              <CheckList>
                <label>
                  <input
                    type="checkbox"
                    checked={draft.body}
                    onChange={(e) => set({ body: e.target.checked })}
                  />
                  Also the request body
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={draft.path}
                    onChange={(e) => set({ path: e.target.checked })}
                  />
                  Also the URL path
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={draft.query}
                    onChange={(e) => set({ query: e.target.checked })}
                  />
                  Also the query string
                </label>
              </CheckList>
              {dest.advanced && (
                <>
                  <CheckList>
                    <label>
                      <input
                        type="checkbox"
                        checked={draft.narrow}
                        onChange={(e) => set({ narrow: e.target.checked })}
                      />
                      Only these headers
                    </label>
                  </CheckList>
                  {draft.narrow && (
                    <LabelledField
                      label="Headers to search"
                      htmlFor={`${idPrefix}-headers`}
                      hint="Separated by commas. No other header is scanned."
                    >
                      <TextInput
                        id={`${idPrefix}-headers`}
                        aria-label="Headers to search"
                        value={draft.headersRaw}
                        placeholder="X-Api-Key, X-Signature"
                        autoComplete="off"
                        spellCheck={false}
                        onChange={(e) => set({ headersRaw: e.target.value })}
                      />
                    </LabelledField>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <LabelledField label="Attach to">
                <SettingsSelect
                  label={`${selectLabel} — destination`}
                  value={draft.via}
                  options={INJECT_VIA_OPTIONS}
                  onChange={(via) => set({ via, nameTouched: false })}
                />
              </LabelledField>
              <LabelledField
                label={
                  draft.via === 'query-param' ? 'Parameter name' : 'Header name'
                }
                htmlFor={`${idPrefix}-inject-name`}
                hint={
                  draft.via === 'query-param'
                    ? 'Required. The query parameter the proxy appends.'
                    : 'Required. The header the proxy sets on each request.'
                }
              >
                <TextInput
                  id={`${idPrefix}-inject-name`}
                  aria-label={
                    draft.via === 'query-param'
                      ? 'Parameter name'
                      : 'Header name'
                  }
                  value={draft.name}
                  placeholder={
                    draft.via === 'query-param' ? 'key' : 'X-Api-Key'
                  }
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={64}
                  onChange={(e) =>
                    set({ name: e.target.value, nameTouched: true })
                  }
                />
              </LabelledField>
              {/* The field the screenshot could not explain. It is the VALUE
                  the name above is set to, and the point of it is the
                  substitution — which the placeholder text hints at and
                  nothing said out loud. */}
              <LabelledField
                label="Value format"
                htmlFor={`${idPrefix}-inject-format`}
                hint={
                  draft.via === 'query-param'
                    ? `Optional. What the parameter is set to, with ${VALUE_TOKEN} replaced by the secret. Leave empty to send the secret on its own.`
                    : `Optional. What the header is set to, with ${VALUE_TOKEN} replaced by the secret. Leave empty to send the secret on its own.`
                }
              >
                <TextInput
                  id={`${idPrefix}-inject-format`}
                  aria-label="Value format"
                  value={draft.formatter}
                  placeholder={`Bearer ${VALUE_TOKEN}`}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(e) => set({ formatter: e.target.value })}
                />
              </LabelledField>
            </>
          )}

          {/* Inside a FieldStatus so it is the same 11px as every other note
              under a field. Rendered bare, it inherited the pane's body size
              and became the loudest thing on screen. */}
          <FieldStatus aria-live="polite">
            {dest.problem != null && <FieldError>{dest.problem}</FieldError>}
            {dest.problem == null &&
              draft.mode === 'replace' &&
              (draft.path || draft.query) && (
                // Not an error — the API accepts both. It is the one thing
                // about this choice a user would want to have been told:
                // paths and query strings routinely land in server access
                // logs, so a secret substituted there is a secret in
                // somebody's log.
                <FieldWarning>
                  Paths and query strings often land in server access logs.
                </FieldWarning>
              )}
          </FieldStatus>

          <GhostButton
            type="button"
            onClick={() => {
              dest.toggleAdvanced();
              onDirty();
            }}
            aria-expanded={dest.advanced}
            aria-label={`${dest.advanced ? 'Hide' : 'Show'} advanced delivery options${scope}`}
          >
            {dest.advanced ? 'Fewer options' : 'More options'}
          </GhostButton>
        </DeliveryColumn>
      </Row>
    </>
  );
}

function errText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message.length > 0
    ? err.message
    : fallback;
}

export function CredentialsSection({
  query = '',
}: {
  /**
   * The Apps & Skills search box, which is one field shared by every tab
   * rather than one per tab. Filtering happens HERE because the list is
   * fetched here — the owning view has no copy of it to filter — and it
   * narrows the list only: it never hides the New button or the intro, so a
   * query that matches nothing still leaves the pane usable.
   *
   * Optional, so the pane still renders standalone (a test, or any host that
   * mounts it outside that view) with nothing filtered.
   */
  readonly query?: string;
} = {}) {
  const { credentials } = useTerminalConfig();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [view, setView] = useState<'list' | 'new'>('list');
  // Survives the trip back from the form. Only the agent-not-told case sets
  // one: a plain success needs no announcement, because the new row appearing
  // in the list IS the announcement.
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (credentials == null) return;
    try {
      setState({ kind: 'ready', credentials: await credentials.list() });
    } catch (err) {
      setState({
        kind: 'error',
        message: errText(err, 'Could not load your credentials.'),
      });
    }
  }, [credentials]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (credentials == null) return null;

  // Enrolling is a detour, not the pane's resting state: the form is four
  // fields and a secret, and showing it above the list made the list — the
  // thing a returning user came for — the part they had to scroll past. So
  // the pane opens on what they have, and the form replaces it when asked
  // for.
  //
  // A second dialog stacked over Settings would say the same thing at the
  // cost of its own backdrop, focus trap and Escape layer. This is already
  // inside a modal; a view swap inside it needs none of that.
  if (view === 'new') {
    return (
      <>
        <FormHeader>
          <BackButton
            type="button"
            onClick={() => setView('list')}
            aria-label="Back to credentials"
          >
            ←
          </BackButton>
          <PaneTitle as="h3">New credential</PaneTitle>
        </FormHeader>
        <PaneIntro>
          Encrypted in this browser before it is sent. Only your own agent’s
          proxy can decrypt it — we cannot read it back, and neither can the
          agent.
        </PaneIntro>

        <AddCredentialForm
          onCancel={() => setView('list')}
          onStored={(credential) => {
            setState((prev) =>
              prev.kind === 'ready'
                ? {
                    kind: 'ready',
                    credentials: [...prev.credentials, credential].sort(
                      (a, b) => a.label.localeCompare(b.label),
                    ),
                  }
                : // The list had not loaded (or had failed); re-fetch rather
                  // than inventing a list from one entry.
                  prev,
            );
            if (state.kind !== 'ready') void reload();
          }}
          onDone={(notice) => {
            // Back to the list, carrying anything the user still needs to
            // read. The agent-not-told warning names the placeholder, which
            // lives on the list row — so the list is where that message
            // belongs, not the form we are leaving.
            setNotice(notice);
            setView('list');
          }}
        />
      </>
    );
  }

  return (
    <>
      <PaneHeader>
        <PaneTitle as="h3">Credentials</PaneTitle>
        <SaveButton
          type="button"
          onClick={() => {
            setNotice(null);
            setView('new');
          }}
        >
          New
        </SaveButton>
      </PaneHeader>
      <PaneIntro>
        API keys, tokens and other secrets your agent can use without ever
        seeing them — an exchange key, a data-provider token, anything that
        belongs in a request header. Each one is encrypted in this browser
        before it is sent, and only your own agent’s proxy can decrypt it: we
        cannot read it back, and neither can the agent.
      </PaneIntro>

      {notice != null && (
        <FieldStatus role="status" aria-live="polite">
          <FieldWarning>{notice}</FieldWarning>
        </FieldStatus>
      )}

      <StoredList
        state={state}
        query={query}
        onNotice={setNotice}
        onRevoked={reload}
        onUpdated={(updated) =>
          setState((prev) =>
            prev.kind === 'ready'
              ? {
                  kind: 'ready',
                  credentials: prev.credentials.map((c) =>
                    c.id === updated.id ? updated : c,
                  ),
                }
              : prev,
          )
        }
      />
    </>
  );
}

function AddCredentialForm({
  onStored,
  onDone,
  onCancel,
}: {
  readonly onStored: (credential: StoredCredentialView) => void;
  /**
   * Leave the form, carrying a message the user still has to read — or null
   * when there is nothing to say. Called only after a successful store: a
   * rejection keeps the form up, with the fields still filled in, because the
   * whole point of the message is to let them fix one and retry.
   */
  readonly onDone: (notice: string | null) => void;
  readonly onCancel: () => void;
}) {
  const { credentials } = useTerminalConfig();
  const [label, setLabel] = useState('');
  const [secret, setSecret] = useState('');
  const dest = useDestination();
  const [form, setForm] = useState<FormState>({ kind: 'idle' });

  // Clears a MESSAGE, and must never clear a submit in flight. 'saving' is
  // not 'idle', so the unguarded version cleared that too — and since this is
  // wired to every field's onChange, typing during a store re-enabled the
  // button and reopened onSubmit's re-entry guard, sealing and POSTing the
  // same plaintext secret twice. The first response unmounts the form, so the
  // second call's error was lost while its success added a second row.
  //
  // The fieldset now disables the fields during a submit, which makes that
  // sequence unreachable through the UI. This is the other half: the state
  // machine itself must have no transition out of 'saving' that anything but
  // the request's own completion can take.
  const clearMessage = (): void => {
    if (form.kind === 'saving') return;
    if (form.kind !== 'idle') setForm({ kind: 'idle' });
  };

  const canonicalLabel = label.trim().toLowerCase();
  const labelValid = LABEL_PATTERN.test(canonicalLabel);
  const valid = labelValid && secret.length > 0 && dest.valid;

  const onSubmit = async (): Promise<void> => {
    const delivery = dest.delivery;
    if (
      credentials == null ||
      !valid ||
      delivery == null ||
      form.kind === 'saving'
    )
      return;
    setForm({ kind: 'saving' });
    try {
      const outcome = await credentials.store({
        label: canonicalLabel,
        allowedHosts: dest.hosts,
        allowedPaths: dest.paths,
        delivery,
        secret,
      });
      if (outcome.kind === 'rejected') {
        // The server's own message: it says which of label, hosts or the
        // per-account limit was the problem, and inventing our own wording
        // here would guess.
        setForm({ kind: 'error', message: outcome.message });
        return;
      }
      // Stored either way — a false agentReady is a note about what is left to
      // do, not a failure to retry. The message travels back to the list,
      // where the placeholder it refers to actually is.
      setForm({ kind: 'idle' });
      setLabel('');
      dest.clear();
      onStored(outcome.credential);
      onDone(
        outcome.agentReady === false
          ? `Stored ${canonicalLabel}, but your agent could not be told. Paste its placeholder below into your conversation, or add another secret to retry publishing.`
          : null,
      );
    } catch (err) {
      setForm({
        kind: 'error',
        message: errText(err, 'Could not store the credential. Try again.'),
      });
    } finally {
      // Cleared on EVERY path, including failure. The other fields are kept on
      // a rejection so the user can fix a label without retyping the key, but
      // the key itself does not sit in memory waiting for a retry — retyping it
      // is a small cost against holding a live secret in a page.
      setSecret('');
    }
  };

  const saving = form.kind === 'saving';

  return (
    <AddForm
      aria-busy={saving}
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit();
      }}
    >
      <FormFields disabled={saving}>
        <Row>
          <RowText>
            <RowLabel as="label" htmlFor="credential-label">
              Name
            </RowLabel>
            {/* The name is not a label on a row: it is the whole of what the
              agent knows about this credential. It becomes CRED_<NAME> in the
              agent's environment and the leading part of the placeholder, and
              nothing else about the credential reaches the model — so
              "binance" and "exchange-2" are the difference between an agent
              that picks the right secret and one that guesses. */}
            <RowHelp>
              Lowercase letters, numbers and hyphens. An accurate name helps
              your agent know when to inject this credential.
            </RowHelp>
          </RowText>
          <FieldColumn>
            <TextInput
              id="credential-label"
              value={label}
              placeholder="binance"
              autoComplete="off"
              spellCheck={false}
              maxLength={64}
              aria-invalid={label.length > 0 && !labelValid}
              onChange={(e) => {
                setLabel(e.target.value);
                clearMessage();
              }}
            />
            <FieldStatus aria-live="polite">
              {label.length > 0 && !labelValid && (
                <FieldError>
                  Use 1–32 characters of a–z, 0–9 and “-”.
                </FieldError>
              )}
            </FieldStatus>
          </FieldColumn>
        </Row>

        <DestinationFields
          dest={dest}
          idPrefix="credential"
          selectLabel="Where this secret goes in the request"
          onDirty={clearMessage}
        />
      </FormFields>

      <Row>
        <RowText>
          <RowLabel as="label" htmlFor="credential-secret">
            Secret
          </RowLabel>
          <RowHelp>
            Encrypted here before it is sent. It is cleared from this page as
            soon as you save, and cannot be shown again afterwards.
          </RowHelp>
        </RowText>
        <FieldColumn>
          <FieldRow as="div">
            <FormFields disabled={saving}>
              <SecretInput
                id="credential-secret"
                // A password field, so it is masked, kept out of the browser's
                // autofill store, and not offered for saving.
                type="password"
                value={secret}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => {
                  setSecret(e.target.value);
                  clearMessage();
                }}
              />
            </FormFields>
            {/* CANCEL STAYS LIVE, deliberately outside the fieldset. It is
                  the only way out of a submit that never settles — the
                  credential calls set no abort signal and no timeout, so a
                  request the server accepts and never answers leaves the form
                  frozen. Nothing about the double-submit guard needs Cancel
                  disabled: a second submit can only come from this form's
                  submit button or from Enter in one of its fields, and both of
                  those are inside the fieldset. */}
            <GhostButton type="button" onClick={onCancel}>
              Cancel
            </GhostButton>
            <SaveButton type="submit" disabled={!valid || saving}>
              {saving ? 'Encrypting…' : 'Store'}
            </SaveButton>
          </FieldRow>
          <FieldStatus role="status" aria-live="polite">
            {form.kind === 'error' && <FieldError>{form.message}</FieldError>}
            {form.kind === 'warning' && (
              <FieldWarning>{form.message}</FieldWarning>
            )}
          </FieldStatus>
        </FieldColumn>
      </Row>
    </AddForm>
  );
}

/**
 * Correcting a stored credential's destination and delivery.
 *
 * Neither the name nor the secret is here, and neither is an oversight. The
 * name is half of what the stored ciphertext is sealed to, so renaming would
 * leave a credential nothing can open — and the failure would land at
 * injection time, on a live request. The secret would need re-sealing in the
 * browser, which is what enrolling a new one already does.
 *
 * What is left is the part users actually get wrong: the header an API really
 * wants, and which hosts a key may reach.
 */
function EditCredentialForm({
  credential,
  onDone,
  onCancel,
}: {
  readonly credential: StoredCredentialView;
  readonly onDone: (credential: StoredCredentialView) => void;
  readonly onCancel: () => void;
}) {
  const { credentials } = useTerminalConfig();
  // Seeded from what is stored, which decides whether this opens on the preset
  // dropdown or on the advanced fields: a config no preset can express opens
  // advanced rather than being unavailable.
  const dest = useDestination({
    hosts: credential.allowedHosts,
    paths: credential.allowedPaths,
    delivery: credential.delivery,
  });
  const [form, setForm] = useState<FormState>({ kind: 'idle' });

  const update = credentials?.update;
  const onSubmit = async (): Promise<void> => {
    const delivery = dest.delivery;
    if (update == null || delivery == null || !dest.valid) return;
    if (form.kind === 'saving') return;
    setForm({ kind: 'saving' });
    try {
      const outcome = await update(credential.id, {
        allowedHosts: dest.hosts,
        allowedPaths: dest.paths,
        delivery,
      });
      if (outcome.kind === 'rejected') {
        setForm({ kind: 'error', message: outcome.message });
        return;
      }
      if (outcome.kind === 'not-found') {
        setForm({
          kind: 'error',
          message: 'That credential is no longer there. Reload the list.',
        });
        return;
      }
      onDone(outcome.credential);
    } catch (err) {
      setForm({
        kind: 'error',
        message: errText(err, 'Could not save the change. Try again.'),
      });
    }
  };

  const saving = form.kind === 'saving';

  return (
    <EditForm
      // Named, so the form is announced as a region and its fields are
      // distinguishable from the add form's identically-labelled ones.
      aria-label={`Edit ${credential.label}`}
      aria-busy={saving}
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit();
      }}
    >
      <FormFields disabled={saving}>
        <DestinationFields
          dest={dest}
          idPrefix={`credential-edit-${credential.id}`}
          selectLabel={`Where the ${credential.label} secret goes in the request`}
          scopeName={credential.label}
          onDirty={() => {
            // Same guard as the add form's clearMessage: no transition out of
            // 'saving' that the request's completion did not make.
            if (form.kind === 'saving') return;
            if (form.kind !== 'idle') setForm({ kind: 'idle' });
          }}
        />
      </FormFields>
      {/* Outside the fieldset for the same reason as the add form's: Cancel
            is the only way out of a save that never settles. */}
      <EditActions>
        <SaveButton type="submit" disabled={!dest.valid || saving}>
          {saving ? 'Saving…' : 'Save'}
        </SaveButton>
        <GhostButton type="button" onClick={onCancel}>
          Cancel
        </GhostButton>
      </EditActions>
      <FieldStatus role="status" aria-live="polite">
        {form.kind === 'error' && <FieldError>{form.message}</FieldError>}
      </FieldStatus>
    </EditForm>
  );
}

function StoredList({
  state,
  query,
  onRevoked,
  onUpdated,
  onNotice,
}: {
  readonly state: LoadState;
  readonly query: string;
  readonly onRevoked: () => Promise<void>;
  readonly onUpdated: (credential: StoredCredentialView) => void;
  readonly onNotice: (message: string) => void;
}) {
  if (state.kind === 'loading') return <ListNote>Loading…</ListNote>;
  if (state.kind === 'error') {
    return (
      <ListNote>
        <FieldError>{state.message}</FieldError>
      </ListNote>
    );
  }
  // "Nothing stored" is checked BEFORE the filter, so an empty account reads
  // as empty rather than as a search that found nothing — the query is not
  // why that list is empty, and telling a first-time user their search
  // matched nothing would send them to fix the wrong thing.
  if (state.credentials.length === 0) {
    return <ListNote>No credentials stored yet.</ListNote>;
  }
  // By NAME only. The name is the whole of what the agent knows about a
  // credential and the only field a user has to recall, and the alternative —
  // matching hosts too — would mean typing a venue and being shown a
  // credential whose name says nothing about it. Hosts are on the row for
  // reading, not for searching.
  const typed = query.trim();
  const shown = state.credentials.filter((c) => looseIncludes(c.label, typed));
  if (shown.length === 0) {
    return <ListNote>No credentials match “{typed}”.</ListNote>;
  }
  return (
    <List>
      {shown.map((c) => (
        <StoredRow
          key={c.id}
          credential={c}
          onRevoked={onRevoked}
          onUpdated={onUpdated}
          onNotice={onNotice}
        />
      ))}
    </List>
  );
}

function StoredRow({
  credential,
  onRevoked,
  onUpdated,
  onNotice,
}: {
  readonly credential: StoredCredentialView;
  readonly onRevoked: () => Promise<void>;
  readonly onUpdated: (credential: StoredCredentialView) => void;
  // For anything the user must still read AFTER this row is gone. A revoke
  // removes the row it was started from, so its own status line cannot carry
  // the result.
  readonly onNotice: (message: string) => void;
}) {
  const { credentials } = useTerminalConfig();
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [status, setStatus] = useState<FormState>({ kind: 'idle' });
  const [copied, setCopied] = useState(false);

  const onRevoke = async (): Promise<void> => {
    if (credentials == null || status.kind === 'saving') return;
    setStatus({ kind: 'saving' });
    try {
      const outcome = await credentials.revoke(credential.id);
      if (outcome.kind === 'partial') {
        // Revoked, but our stored copy is still there. Saying "deleted" would
        // be untrue, and this is exactly the case a user cares about — so it
        // is reported to the PANE, not to this row.
        //
        // The row is about to stop existing. The server stamps the row revoked
        // BEFORE deleting the stored value, so the refetch below no longer
        // returns it, this component unmounts, and a message set on it is
        // never seen. The one test that covered this passed only because its
        // stub kept returning the revoked row.
        onNotice(outcome.message);
      }
      // 'not-found' is success from here: it is already gone.
      await onRevoked();
      // Back to idle even on the way out. If the row DOES come back — a
      // refetch that failed, a partial delete the operator then reverses —
      // leaving it 'saving' would leave Remove permanently inert with no way
      // to retry.
      setStatus({ kind: 'idle' });
    } catch (err) {
      setStatus({
        kind: 'error',
        message: errText(err, 'Could not remove the credential.'),
      });
    }
  };

  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(credential.placeholder);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access can be refused outright. The placeholder is on screen
      // and selectable, so there is nothing to recover from — just don't claim
      // it was copied.
    }
  };

  // Every delivery the API accepts is now expressible in the form — the
  // advanced fields cover the shapes no preset can, and the edit form opens on
  // them when the stored config needs it. So the only question left is whether
  // the backend has the endpoint at all.
  //
  // This used to be gated on the config mapping to an offered preset, because
  // saving would otherwise have narrowed it to the nearest one. That gate is
  // gone with the reason for it.
  const canEdit = credentials?.update != null;

  return (
    <ListItem>
      <ItemMain>
        <ItemLabel>{credential.label}</ItemLabel>
        <ItemMeta>
          {credential.allowedHosts.join(', ')} ·{' '}
          {describeDelivery(credential.delivery)}
        </ItemMeta>
        {showDetails && (
          <DetailList>
            {credentialDetails(credential).map((row) => (
              <Fragment key={row.label}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </Fragment>
            ))}
          </DetailList>
        )}
        {editing && (
          <EditCredentialForm
            credential={credential}
            onDone={(updated) => {
              setEditing(false);
              onUpdated(updated);
            }}
            onCancel={() => setEditing(false)}
          />
        )}
        {/* Inject mode has no placeholder: there is nothing to show and
            nothing for the user to do, so the block is absent rather than
            rendered empty. */}
        {credential.placeholder !== '' && (
          <>
            {/* Labelled, because an unlabelled monospace string next to a
                Copy button says nothing about what it is for. It is the one
                thing on this row a user has to DO something with. */}
            <FieldLabel as="div">Placeholder</FieldLabel>
            <PlaceholderRow>
              <Placeholder title={credential.placeholder}>
                {credential.placeholder}
              </Placeholder>
              <GhostButton
                type="button"
                onClick={() => void onCopy()}
                aria-label={`Copy the placeholder for ${credential.label}`}
              >
                {copied ? 'Copied' : 'Copy'}
              </GhostButton>
            </PlaceholderRow>
            <ItemHelp>
              Instruct your agent to use this placeholder as the credential.
            </ItemHelp>
            {/* Only the negative case says anything. A secret the agent
                already has is the ordinary state and needs no line of its
                own; see agentReady's three values at the top of the file. */}
            {credential.agentReady === false && (
              <ItemHelp>
                <NotYetOnAgent>Not on your agent yet</NotYetOnAgent> — being
                sent now; paste the placeholder if you need it before then.
              </ItemHelp>
            )}
          </>
        )}
        <FieldStatus role="status" aria-live="polite">
          {status.kind === 'error' && <FieldError>{status.message}</FieldError>}
        </FieldStatus>
      </ItemMain>

      <ItemActions>
        {confirming ? (
          <>
            <DangerButton
              type="button"
              onClick={() => void onRevoke()}
              disabled={status.kind === 'saving'}
            >
              {status.kind === 'saving' ? 'Removing…' : 'Confirm'}
            </DangerButton>
            <GhostButton type="button" onClick={() => setConfirming(false)}>
              Cancel
            </GhostButton>
          </>
        ) : (
          <>
            <GhostButton
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              aria-expanded={showDetails}
              aria-label={`${showDetails ? 'Hide' : 'Show'} the full config for ${credential.label}`}
            >
              {showDetails ? 'Hide' : 'Details'}
            </GhostButton>
            {canEdit && !editing && (
              <GhostButton
                type="button"
                onClick={() => setEditing(true)}
                aria-label={`Edit ${credential.label}`}
              >
                Edit
              </GhostButton>
            )}
            {/* Two steps, because this is irreversible: we cannot put the
                secret back, the user has to fetch a fresh one from wherever it
                came from. */}
            <GhostButton type="button" onClick={() => setConfirming(true)}>
              Remove
            </GhostButton>
          </>
        )}
      </ItemActions>
    </ListItem>
  );
}

const PaneIntro = styled.p`
  margin: 0 0 4px;
  ${TYPE.body}
  line-height: 1.5;
  color: var(--text-secondary);
  max-width: 56ch;
`;

/**
 * The pane's own title row: the heading, and the one action that belongs beside
 * it rather than under the content it acts on.
 *
 * `baseline` rather than `center` so the button sits on the heading's text
 * baseline — centring it against a 15px heading reads as slightly high.
 */
const PaneHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 2px;
`;

/**
 * Back out of the form. A button rather than a link: it changes what this pane
 * shows and navigates nowhere, so nothing here should offer a middle-click or
 * a copyable address.
 *
 * Sized against the heading it sits beside, and given a real hit area — an
 * arrow glyph on its own is a 10px target.
 */
const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin: 0 -4px 0 0;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: none;
  ${TYPE.label}
  color: var(--text-secondary);
  cursor: pointer;

  &:hover {
    background: var(--bg-hover, rgba(127, 127, 127, 0.12));
    color: var(--text-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--accent, currentColor);
    outline-offset: 1px;
  }
`;

/**
 * The add form's header: the back arrow on its own line, with the title UNDER
 * it.
 *
 * Not PaneHeader, whose `space-between` is exactly right for the list — a
 * heading at one end and the New button at the other — and exactly wrong here.
 * With the arrow and the title as its two children it pushed the title to the
 * far right of the pane: a heading floating a pane's width away from the
 * control it belongs to, reading as two unrelated things on one line.
 *
 * The arrow is pulled left by roughly its own inner padding, so the glyph —
 * centred in a 24px hit area — lines up with the title's first letter instead
 * of sitting indented from it. Without that the column looks accidentally
 * ragged, which is the failure this layout was meant to fix.
 */
const FormHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  margin-bottom: 2px;

  ${BackButton} {
    margin-left: -6px;
  }
`;

/**
 * The three fields are one form so Enter submits from any of them, and so the
 * browser treats the key field as part of a single credential entry rather than
 * a stray password box.
 */
const AddForm = styled.form`
  display: contents;
`;

/**
 * Everything in a form that a submit in flight must freeze.
 *
 * A FIELDSET, and native `disabled` on it, because that disables every control
 * inside it — including ones added later, which is the part a hand-threaded
 * `disabled` prop on each input gets wrong the first time somebody adds a
 * field. Disabling only the Store button is what this replaces: the button
 * went inert while the label, the secret and every delivery control stayed
 * live, so a second Enter from a text field re-submitted the form and
 * enrolled the same secret twice.
 *
 * `display: contents` so it is invisible to layout — the add form's rows
 * belong to the settings grid two levels up, and a fieldset box in between
 * would take them out of it. The consequence is that it generates no box of
 * its own, so `opacity` on the fieldset does nothing: the dimming is a colour
 * on the fieldset (which still INHERITS to all its text) plus opacity on the
 * controls themselves, which do generate boxes.
 */
const FormFields = styled.fieldset`
  display: contents;
  /* A fieldset's default border, padding and min-width are all unwanted and
     all inherited from the UA sheet. */
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;

  &:disabled {
    color: var(--text-secondary);

    input,
    select,
    button,
    textarea {
      opacity: 0.5;
      cursor: default;
    }
  }
`;

/**
 * The edit form sits inside a list row rather than in the pane's own settings
 * grid, so it lays its rows out itself instead of `display: contents`.
 */
/**
 * Caps the delivery controls at the shared input width. `Row` now wraps the
 * field column under the label rather than letting it squeeze the description
 * (see primitives' `Row`), so this no longer carries that fix on its own — but
 * it still earns its place: wrapping is decided on base sizes, so an uncapped
 * control cluster would push the wrap point wider and stack rows that had room
 * to sit side by side.
 */
const DeliveryColumn = styled(FieldColumn)`
  width: 220px;
  max-width: 100%;
  /* Wider than FieldColumn's 6px: this column stacks whole labelled groups
     rather than one control and its error, so the space between groups has to
     read as larger than the space inside one. */
  gap: 14px;
`;

const EditForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 8px 0 4px;
  padding: 10px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
`;

/**
 * Two columns, labels narrow. A definition list rather than a table: these are
 * label/value pairs about one thing, and dl is what a screen reader reads as
 * such.
 */
const DetailList = styled.dl`
  display: grid;
  grid-template-columns: minmax(0, max-content) minmax(0, 1fr);
  gap: 2px 12px;
  margin: 6px 0 2px;
  ${TYPE.meta}

  dt {
    color: var(--text-label);
  }

  dd {
    margin: 0;
    color: var(--text-secondary);
    /* Header names, formatters and host lists are all values worth reading
       exactly, and a long one must wrap rather than widen the row. */
    overflow-wrap: anywhere;
  }
`;

/**
 * Independent choices, stacked. Checkboxes rather than more select options
 * because a credential may be swapped into a header AND the body, which no
 * single-choice control can say.
 */
const CheckList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  ${TYPE.meta}
  color: var(--text-secondary);

  label {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
  }

  input {
    margin: 0;
    flex-shrink: 0;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

/** The label for a control inside the field column, not beside it. */
const FieldLabel = styled.span`
  ${TYPE.meta}
  color: var(--text-primary);
`;

/** A statement or an explanation — not a field, and not an error. */
const FieldNote = styled.div`
  ${TYPE.meta}
  color: var(--text-secondary);

  strong {
    color: var(--text-primary);
    font-weight: 500;
  }
`;

const EditActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

/**
 * Wider than the shared input: an API key is long and worth seeing the end of.
 * A preference, not a floor — `TextInput`'s `max-width: 100%` still applies and
 * `FieldColumn` now caps itself to the line, so in a narrow embed this yields
 * instead of forcing the row to overflow.
 */
const SecretInput = styled(TextInput)`
  width: 260px;
  font-family: var(--font-mono, monospace);
`;

const List = styled.ul`
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ListNote = styled.div`
  padding: 16px 0 0;
  ${TYPE.body}
  color: var(--text-secondary);
`;

const ListItem = styled.li`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
`;

const ItemMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ItemLabel = styled.div`
  ${TYPE.label}
  color: var(--text-primary);
`;

const ItemMeta = styled.div`
  ${TYPE.meta}
  color: var(--text-secondary);
  overflow-wrap: anywhere;
`;

const ItemHelp = styled.div`
  ${TYPE.meta}
  color: var(--text-label);
`;

/**
 * Not FieldError: the secret is stored and working. The colour says "read
 * this", not "this failed" — the distinction matters, because the two lead to
 * opposite next actions.
 */
const FieldWarning = styled.span`
  color: var(--warning-color, var(--text-primary));
`;

/**
 * The same colour as FieldWarning, for the same reason: this row's secret IS
 * stored and the proxy will substitute it. What is missing is the agent having
 * been told, which the server is already repairing — so this reports a state,
 * it does not ask the user to fix anything.
 */
const NotYetOnAgent = styled.strong`
  color: var(--warning-color, var(--text-primary));
  font-weight: 600;
`;

const PlaceholderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

/**
 * Truncated rather than wrapped, with the copy button as the way anyone actually
 * moves it. The leading "cred-<name>-" is the part worth reading, so the
 * ellipsis falls at the end; the full value is in the title and still
 * selectable — but the random tail is what has to be exact, so anyone reading
 * it off the screen rather than copying it will get a truncated string.
 */
const Placeholder = styled.code`
  ${TYPE.meta}
  font-family: var(--font-mono, monospace);
  color: var(--text-primary);
  background: var(--input-bg-color);
  border-radius: 4px;
  padding: 2px 6px;
  max-width: 28ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const GhostButton = styled.button`
  height: 28px;
  padding: 0 10px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  ${TYPE.meta}
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--accent-color);
    outline-offset: -1px;
  }
`;

const DangerButton = styled(GhostButton)`
  border-color: var(--error-color);
  color: var(--error-color);

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;
